import { collection } from '@/infra/collection'
import {
  TaxRefundMethodVersion,
  TaxRefundMethodVersionId,
} from './TaxRefundMethod'
import type { TaxProfileApi } from '@/features/tax-profile/api/tax-profile-api'
import { Id } from '@/infra/id'
import { debounceTime, groupBy, mergeMap, Subject, switchMap } from 'rxjs'
import { withAbortSignal } from '@/utils/rx-util'
import { onRpcNotFoundReturnNull } from '@/utils/grpc-util'
import { mapTaxRefundMethodProtoToProps } from './tax-refund-method-mappers'
import {
  GetTaxRefundMethodRequest as GetTaxRefundMethodRequestProto,
  GetPersonalTaxRefundMethodForJobRequest as GetPersonalTaxRefundMethodForJobRequestProto,
  GetBusinessTaxRefundMethodForJobRequest as GetBusinessTaxRefundMethodForJobRequestProto,
  CreateTaxRefundMethodRequest as CreateTaxRefundMethodRequestProto,
  UpdateTaxRefundMethodRequest as UpdateTaxRefundMethodRequestProto,
  ListTaxRefundMethodsRequest as ListTaxRefundMethodsRequestProto,
  DelistTaxRefundMethodRequest as DelistTaxRefundMethodRequestProto,
} from '@taxfyle/api-internal/internal/tax_refund_methods_pb'
import {
  mapTaxRefundTypeToProto,
  mapTaxRefundDeliveryMethodToProto,
} from './tax-refund-method-mappers'
import type { TaxRefundType } from './TaxRefundMethod'
import type { TaxRefundDeliveryMethod } from './TaxRefundDeliveryMethod'
import type { GetCurrentMemberFn } from '@/features/iam'
import type { CursorPaged, CursorPageQuery } from '@/infra/paging'

/**
 * Store for managing tax refund methods.
 */
export class TaxRefundMethodStore {
  /**
   * Subject to emit on which triggers a refresh of the latest tax refund method version.
   */
  private readonly _refreshLatestTaxRefundMethodVersion = new Subject<Id>()

  /**
   * Subject to emit when a tax refund method is delisted.
   */
  private readonly _taxRefundMethodDelisted = new Subject<Id>()

  /**
   * Observable that emits when a tax refund method is delisted.
   */
  readonly taxRefundMethodDelisted$ =
    this._taxRefundMethodDelisted.asObservable()

  /**
   * Collection of tax refund method versions.
   */
  readonly taxRefundMethodVersions = collection({
    create: () => new TaxRefundMethodVersion(),
    getDataId: (d) => TaxRefundMethodVersionId.from(d.id, d.version),
    getModelId: (m) => TaxRefundMethodVersionId.from(m.id, m.version),
  })

  constructor(
    private readonly _api: TaxProfileApi,
    private readonly _getCurrentMember: GetCurrentMemberFn,
  ) {
    this._refreshLatestTaxRefundMethodVersion
      .pipe(
        groupBy((methodId) => methodId, {
          duration: (source$) => source$.pipe(debounceTime(5_000)),
        }),
        mergeMap((methodId$) =>
          methodId$.pipe(
            switchMap((methodId) =>
              withAbortSignal((signal) =>
                this.fetchTaxRefundMethod(methodId, signal),
              ),
            ),
          ),
        ),
      )
      .subscribe()
  }

  /**
   * Fetches a tax refund method by ID.
   */
  async fetchTaxRefundMethod(id: Id, signal?: AbortSignal) {
    signal?.throwIfAborted()
    const request = new GetTaxRefundMethodRequestProto().setId(Id.assert(id))
    const response = await this._api.taxRefundMethods
      .getTaxRefundMethod(request)
      .catch(onRpcNotFoundReturnNull)
    signal?.throwIfAborted()
    if (!response) {
      return null
    }
    const props = mapTaxRefundMethodProtoToProps(response)
    return this.taxRefundMethodVersions.set(props)
  }

  /**
   * Fetches the personal tax refund method for a job.
   */
  async fetchPersonalTaxRefundMethodForJob(jobId: Id, signal?: AbortSignal) {
    signal?.throwIfAborted()
    const request = new GetPersonalTaxRefundMethodForJobRequestProto().setJobId(
      Id.assert(jobId),
    )
    const response = await this._api.taxRefundMethods
      .getPersonalTaxRefundMethodForJob(request)
      .catch(onRpcNotFoundReturnNull)
    signal?.throwIfAborted()
    if (!response) {
      return null
    }
    const props = mapTaxRefundMethodProtoToProps(response)
    return this.taxRefundMethodVersions.set(props)
  }

  /**
   * Fetches the business tax refund method for a job.
   */
  async fetchBusinessTaxRefundMethodForJob(jobId: Id, signal?: AbortSignal) {
    signal?.throwIfAborted()
    const request = new GetBusinessTaxRefundMethodForJobRequestProto().setJobId(
      Id.assert(jobId),
    )
    const response = await this._api.taxRefundMethods
      .getBusinessTaxRefundMethodForJob(request)
      .catch(onRpcNotFoundReturnNull)
    signal?.throwIfAborted()
    if (!response) {
      return null
    }
    const props = mapTaxRefundMethodProtoToProps(response)
    return this.taxRefundMethodVersions.set(props)
  }

  /**
   * Creates a new tax refund method.
   */
  async createTaxRefundMethod(input: CreateTaxRefundMethodOptions) {
    const workspaceId = Id.assert(this._getCurrentMember().workspaceId)
    const request = new CreateTaxRefundMethodRequestProto()
      .setWorkspaceId(workspaceId)
      .setRefundType(mapTaxRefundTypeToProto(input.refundType))
      .setDeliveryMethod(
        mapTaxRefundDeliveryMethodToProto(input.deliveryMethod),
      )
    const response =
      await this._api.taxRefundMethods.createTaxRefundMethod(request)

    const id = response.getId()
    const version = response.getVersion()
    const model = this.taxRefundMethodVersions.set({
      id,
      version,
      workspaceId,
      refundType: input.refundType,
      deliveryMethod: input.deliveryMethod,
    })
    this._refreshLatestTaxRefundMethodVersion.next(id)
    return model
  }

  /**
   * Updates an existing tax refund method.
   */
  async updateTaxRefundMethod(input: UpdateTaxRefundMethodOptions) {
    const request = new UpdateTaxRefundMethodRequestProto()
      .setId(Id.assert(input.id))
      .setDeliveryMethod(
        mapTaxRefundDeliveryMethodToProto(input.deliveryMethod),
      )
    const response =
      await this._api.taxRefundMethods.updateTaxRefundMethod(request)

    const id = response.getId()
    const version = response.getVersion()
    const model = this.taxRefundMethodVersions.set({
      id,
      version,
      deliveryMethod: input.deliveryMethod,
    })
    this._refreshLatestTaxRefundMethodVersion.next(id)
    return model
  }

  /**
   * Lists tax refund methods.
   */
  async listTaxRefundMethods(
    query: ListTaxRefundMethodsQuery,
    signal?: AbortSignal,
  ): Promise<CursorPaged<TaxRefundMethodVersion>> {
    signal?.throwIfAborted()
    const workspaceId = Id.assert(this._getCurrentMember().workspaceId)
    const request = new ListTaxRefundMethodsRequestProto().setWorkspaceId(
      workspaceId,
    )

    if (query.refundType) {
      request.setFilter(`refund_type = ${query.refundType.toUpperCase()}`)
    }

    if (query.paging?.limit) {
      request.setPageSize(query.paging.limit)
    }

    if (query.paging?.after) {
      request.setPageToken(query.paging.after)
    }

    const response =
      await this._api.taxRefundMethods.listTaxRefundMethods(request)
    signal?.throwIfAborted()

    const props = response
      .getTaxRefundMethodsList()
      .map((proto) => mapTaxRefundMethodProtoToProps(proto))
    const items = this.taxRefundMethodVersions.set(props)

    return {
      data: items,
      next: response.getNextPageToken() || null,
      total: response.getTotalSize(),
    }
  }

  /**
   * Delists a tax refund method.
   */
  async delistTaxRefundMethod(id: Id) {
    const request = new DelistTaxRefundMethodRequestProto().setId(Id.assert(id))
    await this._api.taxRefundMethods.delistTaxRefundMethod(request)
    this._taxRefundMethodDelisted.next(id)
  }
}

/**
 * Query for listing tax refund methods.
 */
export interface ListTaxRefundMethodsQuery {
  refundType?: TaxRefundType
  paging?: CursorPageQuery
}

/**
 * Options for creating a tax refund method.
 */
export interface CreateTaxRefundMethodOptions {
  refundType: TaxRefundType
  deliveryMethod: TaxRefundDeliveryMethod
}

/**
 * Options for updating a tax refund method.
 */
export interface UpdateTaxRefundMethodOptions {
  id: Id
  deliveryMethod: TaxRefundDeliveryMethod
}
